import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

import { renderManager } from '@maxus/website/contexts/render-manager'
import { configureProjectSwitchConfirmation } from '@maxus/website/lib'

import { useConfirmModal } from '../../base'
import { modificationManager } from '../modification-panel'
import { Notification, type NotificationConfig } from './notification'

// 默认自动消失时长，与 confirm 一样由 provider 统一编排
const AUTO_DISMISS_MS = 3000
// 退出动画时长，需与 index.css 中 maxus-notification-exit-right 的 animation 时长保持一致
const EXIT_ANIMATION_MS = 200

type NotificationContextValue = {
  notify: (config: NotificationConfig) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const { confirm } = useConfirmModal()
  const [config, setConfig] = useState<NotificationConfig | null>(null)
  const [closing, setClosing] = useState(false)
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const removeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = () => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current)
    if (removeTimer.current) clearTimeout(removeTimer.current)
    dismissTimer.current = null
    removeTimer.current = null
  }

  // 先播放退出动画，动画结束后再卸载
  const close = useCallback(
    () => {
      clearTimers()
      setClosing(true)
      removeTimer.current = setTimeout(
        () => {
          setConfig(null)
          setClosing(false)
        },
        EXIT_ANIMATION_MS,
      )
    },
    [],
  )

  const notify = useCallback(
    (next: NotificationConfig) => {
      clearTimers()
      setClosing(false)
      setConfig(next)
      const duration = next.duration ?? AUTO_DISMISS_MS
      if (duration > 0) {
        dismissTimer.current = setTimeout(close, duration)
      }
    },
    [close],
  )

  useEffect(
    () => {
      configureProjectSwitchConfirmation(
        ({ currentProjectName, expectedProjectName }) =>
          new Promise(resolve => {
            confirm({
              cancelLabel: t('Cancel'),
              confirmLabel: t('Open project'),
              description: t(
                'The current DaVinci project {{currentProjectName}} does not match this project. Open {{expectedProjectName}} instead?',
                {
                  currentProjectName,
                  expectedProjectName,
                },
              ),
              onCancel: () => resolve(false),
              onConfirm: () => resolve(true),
              title: t('Switch DaVinci project?'),
            })
          }),
      )
      return () => configureProjectSwitchConfirmation(undefined)
    },
    [confirm, t],
  )

  useEffect(
    () =>
      modificationManager.subscribe(event => {
        switch (event.type) {
          case 'canceled':
            break
          case 'rendering-master':
            notify({ title: t('Rendering master…') })
            break
          case 'master-render-complete':
            notify({ title: t('Master render complete') })
            break
          case 'created':
            notify({ title: t('Created') })
            break
          case 'failed':
            notify({
              status: 'error',
              title: t('Failed'),
              description: event.description,
            })
        }
      }),
    [notify, t],
  )

  useEffect(
    () =>
      renderManager.subscribe(event => {
        switch (event.type) {
          case 'bridge-failed':
            notify({
              status: 'error',
              title: t('Failed'),
              description:
                event.error.kind === 'project_unconfigured'
                  ? t('DaVinci project name is not configured for this project.')
                  : event.error.kind === 'project_mismatch'
                    ? t(
                        "The currently open DaVinci project doesn't match. Please switch to the correct project and try again.",
                      )
                    : event.error.message,
            })
        }
      }),
    [notify, t],
  )

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      {config && <Notification {...config} closing={closing} onClose={close} />}
    </NotificationContext.Provider>
  )
}

const useNotification = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}

export { NotificationProvider, useNotification }
